import { ContentService } from '@blk/cwp-ng-framework';
import { Alignment, BoxModelConstraint, ColumnComposition, ColumnCompositions } from '@blk/simba';
import { CellData, TabData, TableData } from '@ddt-web/data-service';
import { LANDSCAPE_REPORT_TYPES, PortfolioOccurrenceRenderer, ReportType } from '@ddt-web/pdf-utils';
import * as get from 'lodash/get';

import { DefaultColumnComposition } from './default-column-composition';

export class PerformanceColumnComposition extends DefaultColumnComposition {
	public getFundPerfColumnCompositions(
		data: TabData | TableData,
		mode?: ReportType,
		contentService?: ContentService,
		columnProportionsArray?: Array<Array<number>>,
		isCondensedFont: boolean = true
	): ColumnCompositions {
		let tableData: TableData = get(data, 'tableData[0]');
		if (data instanceof TableData) {
			tableData = data;
		}
		const categories: Array<CellData> = tableData.headersY.filter((header: CellData) => get(header, 'config.visible', false));
		const dataHeaders: Array<CellData> = tableData.headersX.filter((header: CellData) => get(header, 'config.visible', false));
		const tableHeaders: Array<CellData> = [tableData.firstColumnTitle, ...dataHeaders];
		const portfolioCount = tableData.portfolioData.filter(portfolio => portfolio).length;
		const noOfColumnsFromData = tableHeaders.length;
		const noOfResultedColumns = noOfColumnsFromData - portfolioCount;
		const showPortfolioOccurrence = LANDSCAPE_REPORT_TYPES.every(reportType => mode !== reportType) && portfolioCount > 1;
		const columnProportions = this.getFundPerfColumnProportions(noOfColumnsFromData, portfolioCount, showPortfolioOccurrence, mode, columnProportionsArray);

		const columnHeaderConfigs = tableHeaders.slice(0, noOfResultedColumns).map(
			(header: CellData, index: number) =>
				new ColumnComposition({
					cells: header,
					widthProportion: columnProportions[index],
					textClasses: isCondensedFont ? ['text-condensed-bold'] : ['text-bold'],
					horizontalAlignment: (index > 1 && get(header, `config.label.attributes.align`) !== 'left') ? Alignment.Horizontal.Right : Alignment.Horizontal.Left,
					verticalAlignment: Alignment.Vertical.Bottom
				})
		);

		if (showPortfolioOccurrence) {
			columnHeaderConfigs.push(
				new ColumnComposition({
					cells: new CellData(null, 'Portfolio Occurrence'),
					widthProportion: columnProportions[noOfResultedColumns],
					textClasses: isCondensedFont ? ['text-condensed-bold'] : ['text-bold'],
					horizontalAlignment: Alignment.Horizontal.Right,
					verticalAlignment: Alignment.Vertical.Bottom
				})
			);
		}

		const columnBodyConfigs = tableData.cells
			.filter((row: Array<CellData>) => get(row[0], 'config.visible', false))
			.map((row: Array<CellData>, index: number) => {
				const bodyConfigs = [
					new ColumnComposition({
						cells: categories[index],
						widthProportion: columnProportions[0],
						textClasses: isCondensedFont ? ['text-condensed-bold'] : ['text-bold'],
						horizontalAlignment: Alignment.Horizontal.Left,
						verticalAlignment: Alignment.Vertical.Middle
					}),
					...row
						.filter((cell: CellData) => get(cell, 'config.visible', false))
						.slice(0, noOfColumnsFromData - portfolioCount - 1)
						.map(
							(cell: CellData, index: number) =>
								new ColumnComposition({
									cells: cell,
									widthProportion: columnProportions[index + 1],
									textClasses: isCondensedFont ? ['text-condensed-light'] : ['text-light'],
									horizontalAlignment: (index > 0 && get(cell, `config.label.attributes.align`) !== 'left') ? Alignment.Horizontal.Right : Alignment.Horizontal.Left,
									verticalAlignment: Alignment.Vertical.Middle,
									boxModelConstraint: {
										paddingTop: get(cell, 'attribute.backgroundColor') ? 6 : 0,
										paddingBottom: get(cell, 'attribute.backgroundColor') ? 6 : 0,
										paddingRight: 1
									},
									cellBoxModelConstraint: {
										backgroundColor: get(cell, 'attribute.backgroundColor'),
										borderLeft: 0.2, borderLeftColor: '#fff',
										borderRight: 0.2, borderRightColor: '#fff',
										marginRight: get(cell, 'config.label.attributes.marginRight') || 0
									}
								})
						)
				];
				if (showPortfolioOccurrence) {
					const filteredRow = row.filter((cell: CellData) => get(cell, 'config.visible', false)).slice(0, noOfColumnsFromData);
					bodyConfigs.push(
						new ColumnComposition({
							cells: filteredRow,
							widthProportion: columnProportions[noOfResultedColumns],
							renderer: new PortfolioOccurrenceRenderer(filteredRow, noOfResultedColumns, Alignment.Horizontal.Center, columnProportions[noOfResultedColumns])
						})
					);
				}
				return bodyConfigs;
			});
		if (!columnHeaderConfigs || !columnBodyConfigs) {
			console.warn('PerformanceColumnComposition is not generated correctly');
		}
		return new ColumnCompositions(columnHeaderConfigs, columnBodyConfigs);
	}

	protected getFundPerfColumnProportions(
		noOfColumnsFromData: number,
		portfolioCount: number,
		showPortfolioOccurrence: boolean,
		mode?: ReportType,
		columnProportionsArray?: Array<Array<number>>
	): Array<number> {
		if (columnProportionsArray) {
			return showPortfolioOccurrence ? columnProportionsArray[0] : columnProportionsArray[1];
		}
		let columnProportions: Array<number>;

		if (showPortfolioOccurrence) {
			switch (noOfColumnsFromData - portfolioCount + 1) {
				case 3:
					columnProportions = [11, 59, 30];
					break;
				case 7:
					columnProportions = [9.5, 37.2, 9.2, 9.3, 12.3, 12.2, 10.3];
					break;
				case 8:
					columnProportions = [8.2, 34.2, 9.2, 9.3, 9.3, 9.2, 10.3, 10.3];
					break;
				case 9:
					columnProportions = [7.2, 32.2, 14.2, 8.2, 7.2, 7.2, 7.3, 7.3, 9.2];
					break;
				case 10:
					columnProportions = [6.2, 30.2, 7.2, 7.2, 7.2, 7.2, 8.3, 8.3, 8.2, 10];
					break;
				case 11:
					columnProportions = [6, 25.1, 7.2, 7.2, 7.2, 8, 8.2, 7.2, 7.2, 7.5, 9.2];
					break;
				case 12:
					columnProportions = [6, 17.3, 7.2, 7.2, 7.2, 8, 8.2, 7.2, 7.2, 7.8, 7.5, 9.2];
					break;
			}
		} else {
			switch (noOfColumnsFromData - portfolioCount) {
				case 3:
					columnProportions = [11, 59, 30];
					break;
				case 6:
					columnProportions = [10.5, 41.2, 10.2, 10.3, 14.3, 13.5];
					break;
				case 7:
					columnProportions = [9.5, 37.2, 9.2, 9.3, 9.3, 13.7, 11.8];
					break;
				case 8:
					columnProportions = [8.2, 34.2, 9.2, 9.3, 9.3, 9.2, 10.3, 10.3];
					break;
				case 9:
					columnProportions = [7.2, 32.2, 8.2, 8.2, 8.2, 8.2, 9.3, 9.3, 9.2];
					break;
				case 10:
					if (LANDSCAPE_REPORT_TYPES.some(reportType => mode === reportType)) {
						columnProportions = [6.0, 27.1, 8.1, 8.1, 8.1, 8.1, 9.3, 9.2, 8.1, 7.9];
					} else {
						columnProportions = [6.2, 27.2, 8.2, 8.2, 8.2, 8.2, 9.3, 8.3, 8.2, 8];
					}
					break;
				case 11:
					if (LANDSCAPE_REPORT_TYPES.some(reportType => mode === reportType)) {
						columnProportions = [6.0, 18.8, 8.1, 8.1, 8.1, 8.1, 9.3, 9.2, 8.1, 8.3, 7.9];
					} else {
						columnProportions = [6.2, 18.9, 8.2, 8.2, 8.2, 8.2, 9.3, 8.3, 8.2, 8.3, 8];
					}
					break;

			}
		}
		return columnProportions;
	}

	public getPerformanceColumnComposition(
		tabData: TabData,
		tableColumnProportions?: Array<number>,
		boxModelConstraint?: Partial<BoxModelConstraint>
	): ColumnCompositions {
		const tableData: TableData = get(tabData, 'tableData[0]');
		const tableHeaders: Array<CellData> = [tableData.firstColumnTitle, ...tableData.headersX];
		const categories: Array<CellData> = tableData.headersY.filter((header: CellData) => get(header, 'config.visible', false));
		const columnProportions: Array<number> = tableColumnProportions || this.getColumnProportions(tabData);

		const headers = tableHeaders
			.filter((header: CellData) => get(header, 'config.visible', false))
			.map((header: CellData, index: number) =>
				new ColumnComposition({
					cells: [header],
					widthProportion: columnProportions[index],
					textClasses: ['text-bold', this.textSize],
					horizontalAlignment: index > 1 ? Alignment.Horizontal.Right : Alignment.Horizontal.Left,
					verticalAlignment: Alignment.Vertical.Bottom,
					boxModelConstraint: !!index ? this.boxModel : this.firstColumnBoxModel
				})
			);

		const body = tableData.cells
			.filter((row: Array<CellData>) => get(row[0], 'config.visible', false))
			.map((row: Array<CellData>, index: number) => {
				const firstCol = new ColumnComposition({
					cells: categories[index],
					widthProportion: columnProportions[0],
					textClasses: [this.textSize],
					verticalAlignment: Alignment.Vertical.Middle,
					horizontalAlignment: Alignment.Horizontal.Left,
					boxModelConstraint: this.firstColumnBoxModel
				});

				return [
					firstCol,
					...row
						.filter((cell: CellData, index: number) => {
							const tableHeading = tableHeaders[index + 1]; // +1 to skip the portfolio name column
							return get(tableHeading, 'config.visible', false);
						})
						.map(
							(cell: CellData, cellIndex: number) =>
								new ColumnComposition({
									cells: [cell],
									widthProportion: columnProportions[cellIndex + 1],
									textClasses: [this.textSize],
									verticalAlignment: Alignment.Vertical.Middle,
									horizontalAlignment: cellIndex === 0 ? Alignment.Horizontal.Left : Alignment.Horizontal.Right,
									boxModelConstraint: boxModelConstraint || this.boxModel
								})
						)];
			});

		return new ColumnCompositions(headers, body);
	}
}
